import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  TextField,
} from "@material-ui/core";
import PropTypes from "prop-types";
import React from "react";
import { useDispatch } from "react-redux";

import {
  selectRemoteAccess,
  selectEnableTime,
} from "../../../model/RemoteMaintenance";
import { fetchUpdateMachine } from "../../../store/RemoteMaintenanceStore";
import useStyles from "../useStyles";

/**
 * リモート接続ダイアログコンポーネント.
 *
 * @param {boolean} open ダイアログの表示状態
 * @param {object} machineInfo 選択中の機器情報
 * @param {function} onClose ダイアログクローズ時の処理
 * @return {object} コンポーネント
 */
function RemoteAccessDialog(props) {
  // 引数
  const { open, machineInfo, onClose } = props;

  // スタイル
  const styles = useStyles();

  // Dispatch
  const dispatch = useDispatch();

  // ダイアログによる設定項目(リモート設定、クライアントIP、有効期限)
  const [value, setValue] = React.useState({
    remoteAccess: false,
    clientIp: "-",
    enableTime: 3,
  });
  const [backup, setBackup] = React.useState({
    remoteAccess: false,
    clientIp: "-",
    enableTime: 3,
  });
  const [ipCheck, setIpCheck] = React.useState(false);

  // ラジオグループのリファレンス
  const remoteAccessRef = React.useRef(null);
  const enableTimeRef = React.useRef(null);

  // ラジオボタンへのフォーカス
  const handleEntering = () => {
    if (remoteAccessRef.current != null) {
      remoteAccessRef.current.focus();
    }
    if (enableTimeRef.current != null) {
      enableTimeRef.current.focus();
    }
  };

  React.useEffect(() => {
    setValue({
      remoteAccess: machineInfo.remoteAccess || false,
      clientIp: machineInfo.clientIp || "",
      enableTime: machineInfo.enableTime || 3,
    });
    setBackup({
      remoteAccess: machineInfo.remoteAccess || false,
      clientIp: machineInfo.clientIp || "",
      enableTime: machineInfo.enableTime || 3,
    });
  }, [
    machineInfo.serialNumber,
    machineInfo.remoteAccess,
    machineInfo.clientIp,
    machineInfo.enableTime,
  ]);

  React.useEffect(() => {
    if (value.clientIp.match(/^\d{1,3}(\.\d{1,3}){3}$/)) {
      setIpCheck(true);
    } else {
      setIpCheck(false);
    }
  }, [value.clientIp]);

  // キャンセルボタン押下時の処理
  const handleCancel = () => {
    setValue(backup);
    onClose();
  };

  // OKボタン押下時の処理
  const handleOk = () => {
    // 機器設定情報の更新(&再取得)
    const payload = {
      serialNumber: machineInfo.serialNumber,
      remoteAccess: value.remoteAccess,
      clientIp: value.clientIp,
      enableTime: value.enableTime,
    };
    dispatch(fetchUpdateMachine(payload));
    onClose();
  };

  // リモート設定(ラジオボタン)変更時の処理
  const handleChangeAccess = (event) => {
    if (event && event.target && event.target.value) {
      setValue({ ...value, remoteAccess: event.target.value === "true" });
    }
  };

  // クライアントIP変更時の処理
  const handleChangeIp = (event) => {
    if (event && event.target) {
      setValue({ ...value, clientIp: event.target.value || "" });
    }
  };

  // 有効期限変更時の処理
  const handleChangeTime = (event) => {
    if (event && event.target && event.target.value) {
      setValue({ ...value, enableTime: Number(event.target.value) });
    }
  };

  return (
    <Dialog
      maxWidth="sm"
      TransitionProps={{
        onEntering: handleEntering,
      }}
      onClose={handleCancel}
      open={open}
      fullWidth
    >
      <DialogTitle>
        {machineInfo.iotName} ({machineInfo.serialNumber} : {machineInfo.label})
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <FormControl>
          <FormLabel>リモート接続 : </FormLabel>
          <RadioGroup
            row
            ref={remoteAccessRef}
            value={value.remoteAccess}
            onChange={handleChangeAccess}
          >
            {selectRemoteAccess.map((setting) => (
              <FormControlLabel
                key={setting.key}
                value={setting.value}
                label={setting.label}
                control={<Radio />}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogContent className={styles.dialogContent}>
        <FormControl>
          <FormLabel>有効期間 : </FormLabel>
          <RadioGroup
            row
            ref={enableTimeRef}
            value={value.enableTime}
            onChange={handleChangeTime}
          >
            {selectEnableTime.map((select) => (
              <FormControlLabel
                key={select.key}
                value={select.value}
                label={select.label}
                control={<Radio />}
                disabled={machineInfo.remoteAccess}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogContent className={styles.dialogContent}>
        <FormControl style={{ height: 70 }}>
          <FormLabel>接続元IPアドレス : </FormLabel>
          <TextField
            id="ipAddress"
            type="text"
            value={value.clientIp}
            variant="standard"
            onChange={handleChangeIp}
            disabled={machineInfo.remoteAccess}
            error={value.remoteAccess && !ipCheck}
            helperText={
              value.remoteAccess && !ipCheck ? "IPアドレスの形式が不正です" : ""
            }
          />
        </FormControl>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button color="default" onClick={handleCancel}>
          キャンセル
        </Button>
        <Button
          color="primary"
          onClick={handleOk}
          disabled={value.remoteAccess && !ipCheck}
        >
          設定
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RemoteAccessDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  machineInfo: PropTypes.shape({
    serialNumber: PropTypes.string.isRequired,
    iotName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    power: PropTypes.bool.isRequired,
    boxIp: PropTypes.string.isRequired,
    remoteAccess: PropTypes.bool.isRequired,
    clientIp: PropTypes.string.isRequired,
    enableTime: PropTypes.number.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RemoteAccessDialog;
